/**
 * 전역 에러 핸들러 설정
 * 모듈 로딩 단계 및 런타임 에러를 포착하여 콘솔과 UI에 표시
 * 
 * 이 모듈은 React Error Boundary가 포착하지 못하는 에러를 처리합니다:
 * - 모듈 로딩 단계의 동기 에러
 * - Promise rejection
 * - 기타 전역 에러
 */

/**
 * 전역 에러 핸들러 설정
 * index.js에서 가장 먼저 호출되어야 합니다.
 */
export function setupGlobalErrorHandler() {
  // window.onerror: 동기 에러 및 모듈 로딩 에러 포착
  window.addEventListener('error', (event) => {
    console.error('[Global Error Handler] 모듈 로딩 에러:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
      stack: event.error?.stack
    });
    
    showErrorUI({
      title: '모듈 로딩 에러',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack
    });
    
    // 기본 에러 처리도 실행 (콘솔에 출력되도록)
    return false;
  });
  
  // unhandledrejection: Promise rejection 포착
  window.addEventListener('unhandledrejection', (event) => {
    console.error('[Global Error Handler] Promise rejection:', {
      reason: event.reason,
      promise: event.promise,
      stack: event.reason?.stack
    });
    
    showErrorUI({
      title: 'Promise Rejection',
      message: event.reason?.message || String(event.reason),
      stack: event.reason?.stack
    });
    
    // 기본 에러 처리 방지하지 않음 (콘솔에도 출력되도록)
    // event.preventDefault()를 호출하지 않음
  });
}

/**
 * 에러 정보를 화면에 표시
 * @param {Object} errorInfo - 에러 정보
 * @param {string} errorInfo.title - 에러 제목
 * @param {string} errorInfo.message - 에러 메시지
 * @param {string} [errorInfo.filename] - 파일명
 * @param {number} [errorInfo.lineno] - 라인 번호
 * @param {number} [errorInfo.colno] - 컬럼 번호
 * @param {string} [errorInfo.stack] - 스택 트레이스
 */
function showErrorUI({ title, message, filename, lineno, colno, stack }) {
  // 기존 에러 UI 제거
  const existing = document.getElementById('global-error-display');
  if (existing) {
    existing.remove();
  }
  
  // document.body가 준비되지 않은 경우 대기
  if (!document.body) {
    // DOMContentLoaded 이벤트 대기
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        showErrorUI({ title, message, filename, lineno, colno, stack });
      });
      return;
    }
    // 이미 로드된 경우 약간의 지연 후 재시도
    setTimeout(() => {
      showErrorUI({ title, message, filename, lineno, colno, stack });
    }, 100);
    return;
  }
  
  const errorDisplay = document.createElement('div');
  errorDisplay.id = 'global-error-display';
  errorDisplay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #ff4444;
    color: white;
    padding: 20px;
    z-index: 10000;
    font-family: monospace;
    max-height: 50vh;
    overflow: auto;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
    font-size: 14px;
    line-height: 1.5;
  `;
  
  const locationInfo = filename 
    ? `${filename}${lineno ? `:${lineno}${colno ? `:${colno}` : ''}` : ''}`
    : '';
  
  errorDisplay.innerHTML = `
    <h2 style="margin: 0 0 10px 0; font-size: 18px;">⚠️ ${title}</h2>
    <p style="margin: 5px 0;"><strong>에러:</strong> ${escapeHtml(message)}</p>
    ${locationInfo ? `<p style="margin: 5px 0;"><strong>위치:</strong> ${escapeHtml(locationInfo)}</p>` : ''}
    ${stack ? `
      <details style="margin: 10px 0;">
        <summary style="cursor: pointer; font-weight: bold; margin-bottom: 5px;">스택 트레이스</summary>
        <pre style="background: rgba(0,0,0,0.3); padding: 10px; margin: 5px 0; overflow: auto; white-space: pre-wrap; word-wrap: break-word;">${escapeHtml(stack)}</pre>
      </details>
    ` : ''}
    <div style="margin-top: 15px;">
      <button onclick="window.location.reload()" style="
        background: white;
        color: #ff4444;
        border: none;
        padding: 10px 20px;
        cursor: pointer;
        font-weight: bold;
        margin-right: 10px;
        border-radius: 4px;
      ">새로고침</button>
      <button onclick="document.getElementById('global-error-display')?.remove()" style="
        background: rgba(255,255,255,0.2);
        color: white;
        border: 1px solid white;
        padding: 10px 20px;
        cursor: pointer;
        border-radius: 4px;
      ">닫기</button>
    </div>
  `;
  
  document.body.appendChild(errorDisplay);
}

/**
 * HTML 이스케이프 유틸리티
 * @param {string} text - 이스케이프할 텍스트
 * @returns {string} 이스케이프된 텍스트
 */
function escapeHtml(text) {
  if (typeof text !== 'string') {
    return String(text);
  }
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
